import React, { type ComponentProps, forwardRef } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import { variant as createVariants } from 'styled-system';

import { componentCss } from '../../internal/styles/componentCss';
import { Box } from '../../layouts';
import type { FadeProps } from './types';

const fadeIn = keyframes`
  from {
    opacity: 0;
    display: none;
  }
  to {
    opacity: 1;
    display: initial;
  },
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    display: initial;
  }
  to {
    opacity: 0;
    display: none;
  }
`;

const FadeBox = styled(Box)<FadeProps>(
  componentCss({
    animationDirection: 'normal',
    animationDuration: '.4s',
    animationFillMode: 'both',
    animationTimingFunction: 'ease-in-out',
    transformStyle: 'preserve-3d',
    position: 'fixed',
    backgroundColor: 'backDrop.background',
    width: '100%',
    height: '100%',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 'modal',
  }),
  createVariants({
    prop: 'type',
    variants: {
      blurred: {
        backdropFilter: 'blur(5px)',
      },
      transparent: {
        backgroundColor: 'transparent',
      },
    },
  }),
  ({ show }) =>
    css({
      animationName: `${show ? fadeIn : fadeOut}`,
      display: show ? 'initial' : 'none',
    })
);

const Fade = forwardRef<HTMLDivElement, ComponentProps<typeof FadeBox>>(
  ({ show, ...rest }, ref) => <FadeBox show={show} ref={ref} {...rest} />
);

export default Fade;
